import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getVehiclesSuggestions,
  clearSuggestions,
} from "../../store/slices/vehiclesSlice";
import { VehicleSuggestion } from "../../types/vehicles";
import { X, Truck, Hash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { vehicleLabel, vehiclePlateHint } from "../../utils/vehicleLabel";

type SearchField = "truckLicensePlate" | "trailerLicensePlate";

interface VehicleFilterProps {
  selectedCarrier: string[];
  onCarrierSelect: (carrier: string) => void;
  onCarrierRemove: (carrierId: string) => void;
  searchField?: SearchField;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

const iconForField: Record<SearchField, React.ElementType> = {
  truckLicensePlate: Truck,
  trailerLicensePlate: Hash,
};

const VehicleFilter: React.FC<VehicleFilterProps> = ({
  selectedCarrier,
  onCarrierSelect,
  onCarrierRemove,
  searchField = "truckLicensePlate",
  placeholder = "Search carrier...",
  disabled = false,
  error = false,
  className = "",
}) => {
  const { t } = useTranslation("shipments");

  const dispatch = useAppDispatch();
  const { suggestions, loading } = useAppSelector((state) => state.vehicles);

  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // The filter is by plate; this remembers what each picked plate is called,
  // so its chip can show the display name. A plate restored from elsewhere
  // has no entry and shows as itself.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const filteredSuggestions = suggestions.filter(
    (carrier) => !selectedCarrier.some((selected) => selected === carrier._id),
  );

  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (term.trim().length >= 2) {
          dispatch(
            getVehiclesSuggestions({
              query: term.trim(),
              limit: 10,
              type: searchField === "truckLicensePlate" ? "TRUCK" : "TRAILER",
            }),
          );
          setShowSuggestions(true);
        } else {
          dispatch(clearSuggestions());
          setShowSuggestions(false);
        }
      }, 750);
    },
    [dispatch, searchField],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dispatch(clearSuggestions());
    };
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(-1);
    debouncedSearch(newValue);
  };

  const handleCarrierSelect = (carrier: VehicleSuggestion) => {
    setLabels((prev) => ({ ...prev, [carrier.registrationNumber]: vehicleLabel(carrier) }));
    onCarrierSelect(carrier.registrationNumber);
    setSearchTerm("");
    setShowSuggestions(false);
    dispatch(clearSuggestions());
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredSuggestions.length
        ) {
          handleCarrierSelect(filteredSuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const Icon = iconForField[searchField];

  return (
    <div ref={wrapperRef} className={`w-full ${className}`}>
      {/* Input */}
      <div className="relative">
        <Icon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-dark-300 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={`w-full pl-7 pr-7 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-dark-300 transition-colors
            ${error ? "border-red-300 focus:ring-red-500" : "border-dark-300"}
            ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
          `}
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && searchTerm && filteredSuggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((carrier, index) => (
            <button
              key={carrier._id}
              onClick={() => handleCarrierSelect(carrier)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                index === highlightedIndex ? "bg-gray-100" : ""
              }`}
            >
              {vehicleLabel(carrier)}
              {vehiclePlateHint(carrier) && (
                <span className="ml-1 text-gray-400">· {vehiclePlateHint(carrier)}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {showSuggestions &&
        filteredSuggestions.length === 0 &&
        !loading &&
        searchTerm.length >= 2 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-dark-400 text-center">
            {t("filters.noResults")} "{searchTerm}"
          </div>
        )}

      {/* Selected chips */}
      {selectedCarrier.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedCarrier.map((carrier, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-dark-100 rounded-full text-xs"
            >
              <span className="text-dark-600 font-medium">
                {labels[carrier] ?? carrier}
              </span>
              <button
                type="button"
                onClick={() => onCarrierRemove(carrier)}
                disabled={disabled}
                className="text-dark-400 hover:text-dark-600 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleFilter;
